import React, { useState } from 'react';
import { useCart, PRODUCTS } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import brushGreen from '../assets/images/brush-green.jpg';
import brushPurple from '../assets/images/brush-purple.jpg';
import brushRose from '../assets/images/brush-rose.jpg';

// Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxJz49LbeuEG1rBB5D4xJQQuzk0jWLsEuJ5CvhnP2wKU32QCX-QxyKaJI0-CpnpZyI/exec';

const OrderForm = () => {
  const { selectedVariant } = useCart();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  
  const product = PRODUCTS[selectedVariant] || PRODUCTS['set'];
  const colorOptions = [
    { id: 'rose', label: t.orderForm.colors?.rose || 'Rose', swatch: '#E6A0BC', image: brushRose },
    { id: 'purple', label: t.orderForm.colors?.purple || 'Purple', swatch: '#C2B5F2', image: brushPurple },
    { id: 'green', label: t.orderForm.colors?.green || 'Green', swatch: '#B9D5B3', image: brushGreen },
  ];
  const [colorQuantities, setColorQuantities] = useState(() =>
    colorOptions.reduce((acc, color, index) => {
      acc[color.id] = index === 0 ? 1 : 0;
      return acc;
    }, {})
  );
  const [activeColorId, setActiveColorId] = useState(() => colorOptions[0]?.id || 'rose');
  const totalQuantity = colorOptions.reduce((sum, color) => sum + (colorQuantities[color.id] || 0), 0);
  const selectedColors = colorOptions.filter((color) => (colorQuantities[color.id] || 0) > 0);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  const shippingOptions = [
    { id: 'dhaka-inside', label: t.orderForm.shippingOptions?.insideDhaka || 'Inside Dhaka City', price: 50 },
    { id: 'dhaka-outside', label: t.orderForm.shippingOptions?.outsideDhaka || 'Outside Dhaka City', price: 80 },
    { id: 'outside-dhaka', label: t.orderForm.shippingOptions?.outsideDistrict || 'Outside Dhaka District', price: 100 },
  ];
  const [selectedShippingId, setSelectedShippingId] = useState(() => shippingOptions[0]?.id || 'dhaka-inside');
  const selectedShipping = shippingOptions.find((option) => option.id === selectedShippingId) || shippingOptions[0];

  // Calculate totals
  const subtotal = product.price * totalQuantity;
  const deliveryCharge = selectedShipping?.price ?? 0;
  const grandTotal = subtotal + deliveryCharge;
  const discountPercent = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: cleaned.slice(0, 11) }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = t.checkout.nameRequired;
    if (!formData.phone) {
      newErrors.phone = t.checkout.phoneRequired;
    } else if (!/^01[3-9]\d{8}$/.test(formData.phone)) {
      newErrors.phone = t.checkout.phoneInvalid;
    }
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = t.checkout.emailInvalid;
    }
    if (!formData.address.trim()) newErrors.address = t.checkout.addressRequired;
    if (totalQuantity <= 0) newErrors.color = t.orderForm.colorRequired;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleColorQuantity = (colorId, nextQuantity) => {
    const nextValue = Math.max(0, nextQuantity);
    setColorQuantities(prev => {
      const updated = { ...prev, [colorId]: nextValue };
      const nextSelected = colorOptions.find((color) => (updated[color.id] || 0) > 0);
      if (nextValue > 0) {
        setActiveColorId(colorId);
      } else if (activeColorId === colorId) {
        setActiveColorId(nextSelected?.id || colorOptions[0]?.id || 'rose');
      }
      return updated;
    });
    if (errors.color) {
      setErrors(prev => ({ ...prev, color: '' }));
    }
  };

  const handleColorCardTap = (colorId, currentQuantity) => {
    if (currentQuantity > 0) {
      handleColorQuantity(colorId, currentQuantity - 1);
      return;
    }
    handleColorQuantity(colorId, 1);
  };

  const submitToGoogleSheets = async (orderData) => {
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      return { success: true };
    } catch (error) {
      console.error('Error submitting to Google Sheets:', error);
      return { success: false, error: error.message };
    }
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    
    setIsProcessing(true);
    setSubmitError('');
    
    const newOrderNumber = `LA-${Date.now().toString().slice(-6)}${Math.floor(Math.random() * 90 + 10)}`;
    
    const colorSummary = selectedColors
      .map((color) => `${color.label} x${colorQuantities[color.id]}`)
      .join(', ');

    const orderData = {
      orderId: newOrderNumber,
      name: formData.fullName,
      phone: `+88${formData.phone}`,
      email: formData.email || 'N/A',
      address: formData.address,
      products: `${product.name} - ${colorSummary}`,
      quantity: totalQuantity,
      subtotal: subtotal,
      delivery: deliveryCharge,
      total: grandTotal,
      notes: formData.notes || 'N/A'
    };
    
    try {
      const result = await submitToGoogleSheets(orderData);
      if (result.success) {
        setOrderNumber(newOrderNumber);
        setOrderPlaced(true);
        if (window.fbq) {
          window.fbq('track', 'Purchase');
        }
      } else {
        setSubmitError(t.checkout.submitError);
      }
    } catch (error) {
      setSubmitError(t.checkout.submitError);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNewOrder = () => {
    setOrderPlaced(false);
    setOrderNumber('');
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      address: '',
      notes: ''
    });
    setColorQuantities(
      colorOptions.reduce((acc, color, index) => {
        acc[color.id] = index === 0 ? 1 : 0;
        return acc;
      }, {})
    );
    setActiveColorId(colorOptions[0]?.id || 'rose');
  };

  // Input styling classes
  const inputBaseStyles = `w-full min-h-[48px] px-4 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20`;
  const inputLightStyles = `bg-white border-[#f2bfd4] text-[#2d1c2b] placeholder-[#b08aa0] focus:border-primary`;
  const inputDarkStyles = `bg-slate-800 border-slate-600 text-white placeholder-slate-400 focus:border-primary`;
  const inputErrorStyles = `border-red-500 focus:border-red-500 focus:ring-red-500/20`;

  return (
    <section 
      id="order-form" 
      className={`py-8 lg:py-10 px-4 sm:px-6 lg:px-8 ${isDark ? 'bg-slate-900' : 'bg-[#ffe7f1]'}`}
      aria-labelledby="order-form-title"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-10">
          <h2 
            id="order-form-title"
            className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}
          >
            <span role="img" aria-label="Shopping cart">🛒</span> {t.orderForm.title}
          </h2>
        </div>

        {!orderPlaced ? (
          <form 
            onSubmit={(e) => { e.preventDefault(); handlePlaceOrder(); }}
            className={`rounded-2xl border p-6 sm:p-8 shadow-card ${
              isDark 
                ? 'bg-slate-800 border-slate-700' 
                : 'bg-white/80 border-[#f2bfd4]'
            }`}
          >
            
            {/* Product Selection */}
            <fieldset className="mb-10">
              <legend className={`font-bold text-lg mb-4 ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                {t.orderForm.yourProducts}
              </legend>
              
              <div className={`relative overflow-hidden rounded-2xl border-2 p-4 sm:p-5 ${
                isDark 
                  ? 'bg-slate-700/40 border-slate-600' 
                  : 'bg-white/80 border-[#f2bfd4] shadow-[0_20px_40px_-30px_rgba(226,76,139,0.55)]'
              }`}>
                <div className={`pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl ${
                  isDark ? 'bg-primary/10' : 'bg-[#ffd6e6]'
                }`} />
                <div className="relative flex flex-col gap-3">
                  <div className="space-y-3">
                    {colorOptions.map((color) => {
                      const colorQty = colorQuantities[color.id] || 0;
                      const isSelected = colorQty > 0;
                      const quantityLabel = t.orderForm.quantity || 'Quantity';
                      const cardActionLabel = isSelected
                        ? (t.orderForm.cardTapToDecrease || 'Tap to decrease quantity by 1.')
                        : (t.orderForm.cardTapToAdd || 'Tap to add this color.');
                      const colorCardAriaLabel = `${product.name} ${color.label}. ${quantityLabel}: ${colorQty}. ${cardActionLabel}`;
                      return (
                        <div
                          key={color.id}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          aria-label={colorCardAriaLabel}
                          onClick={() => handleColorCardTap(color.id, colorQty)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              if (event.key === ' ') {
                                event.preventDefault();
                              }
                              handleColorCardTap(color.id, colorQty);
                            }
                          }}
                          className={`flex flex-col gap-2 rounded-xl border p-3 sm:p-4 transition-all cursor-pointer ${
                            isSelected
                              ? isDark
                                ? 'border-primary bg-primary/10 shadow-[0_12px_24px_-18px_rgba(226,76,139,0.6)]'
                                : 'border-[#E24C8B] bg-white shadow-[0_12px_24px_-18px_rgba(226,76,139,0.45)]'
                              : isDark
                                ? 'border-slate-600 bg-slate-800/60 hover:border-primary/50'
                                : 'border-[#f2bfd4] bg-white/80 hover:border-[#E24C8B]/50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={`mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                                isSelected
                                  ? 'border-primary'
                                  : isDark
                                    ? 'border-slate-500'
                                    : 'border-[#d8a4bb]'
                              }`}
                              aria-hidden="true"
                            >
                              <span className={`h-2 w-2 rounded-full ${isSelected ? 'bg-primary' : 'bg-transparent'}`} />
                            </span>
                            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 ${
                              isDark ? 'border-slate-600' : 'border-[#f2bfd4]'
                            }`}>
                              <img
                                src={color.image}
                                alt={`${product.name} - ${color.label}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <div className="flex min-w-0 flex-1 items-start justify-between gap-3 sm:items-center">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                                    {product.name}
                                  </p>
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                  isDark ? 'bg-slate-700 text-slate-200' : 'bg-[#fff1f7] text-[#b24a7a]'
                                }`}>
                                  {color.label}
                                </span>
                              </div>
                              <p className={`text-[11px] sm:text-sm truncate sm:whitespace-normal sm:overflow-visible sm:text-clip ${isDark ? 'text-slate-300' : 'text-[#7b5a6a]'}`}>
                                {t.setContents?.title || product.size}
                              </p>
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <span className="text-lg sm:text-xl font-bold text-primary">৳{product.price}</span>
                                {product.originalPrice && (
                                  <span className="text-xs sm:text-sm line-through text-[#b08aa0]">৳{product.originalPrice}</span>
                                )}
                                {discountPercent > 0 && (
                                  <span className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                    {discountPercent}% OFF
                                  </span>
                                )}
                              </div>
                              </div>
                              <div className={`flex items-center gap-3 rounded-full border px-3 py-1.5 shrink-0 ${
                                isDark ? 'border-slate-600 bg-slate-800/70' : 'border-[#f2bfd4] bg-white'
                              }`}>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleColorQuantity(color.id, colorQty - 1);
                                  }}
                                  aria-label={`Decrease ${color.label} quantity`}
                                  disabled={colorQty <= 0}
                                  className={`text-base font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                                    isDark ? 'text-slate-200 hover:text-primary' : 'text-[#4b3a2a] hover:text-primary'
                                  }`}
                                >
                                  -
                                </button>
                                <span className={`min-w-[2ch] text-center text-sm font-semibold ${
                                  isDark ? 'text-slate-200' : 'text-[#2d1c2b]'
                                }`}>
                                  {colorQty}
                                </span>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleColorQuantity(color.id, colorQty + 1);
                                  }}
                                  aria-label={`Increase ${color.label} quantity`}
                                  className={`text-base font-bold transition-all ${
                                    isDark ? 'text-slate-200 hover:text-primary' : 'text-[#4b3a2a] hover:text-primary'
                                  }`}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {errors.color && (
                    <p className="text-xs font-semibold text-red-500">{errors.color}</p>
                  )}
                </div>
              </div>
            </fieldset>

            {/* Billing Details */}
            <fieldset className="mb-10">
              <legend className={`font-bold text-lg mb-4 ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                {t.orderForm.billingDetails}
              </legend>
              
              <div className="space-y-5">
                {/* Full Name */}
                <div>
                  <label 
                    htmlFor="fullName"
                    className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-[#4b3a2a]'}`}
                  >
                    {t.orderForm.yourName} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder={t.checkout.fullName}
                    aria-required="true"
                    aria-invalid={!!errors.fullName}
                    aria-describedby={errors.fullName ? "fullName-error" : undefined}
                    className={`${inputBaseStyles} ${isDark ? inputDarkStyles : inputLightStyles} ${errors.fullName ? inputErrorStyles : ''}`}
                  />
                  {errors.fullName && (
                    <p id="fullName-error" className="text-red-500 text-sm mt-2 flex items-center gap-1" role="alert">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.fullName}
                    </p>
                  )}
                </div>

                {/* Phone + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label 
                      htmlFor="phone"
                      className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-[#4b3a2a]'}`}
                    >
                      {t.orderForm.mobileNumber} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-medium ${isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}`}>
                        +88
                      </span>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="01XXXXXXXXX"
                        aria-required="true"
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? "phone-error" : undefined}
                        className={`${inputBaseStyles} pl-14 ${isDark ? inputDarkStyles : inputLightStyles} ${errors.phone ? inputErrorStyles : ''}`}
                      />
                    </div>
                    {errors.phone && (
                      <p id="phone-error" className="text-red-500 text-sm mt-2 flex items-center gap-1" role="alert">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label 
                      htmlFor="email"
                      className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-[#4b3a2a]'}`}
                    >
                      {t.checkout.email}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      className={`${inputBaseStyles} ${isDark ? inputDarkStyles : inputLightStyles} ${errors.email ? inputErrorStyles : ''}`}
                    />
                    {errors.email && (
                      <p id="email-error" className="text-red-500 text-sm mt-2 flex items-center gap-1" role="alert">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label 
                    htmlFor="address"
                    className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-[#4b3a2a]'}`}
                  >
                    {t.orderForm.yourAddress} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder={t.checkout.addressPlaceholder}
                    aria-required="true"
                    aria-invalid={!!errors.address}
                    aria-describedby={errors.address ? "address-error" : undefined}
                    className={`${inputBaseStyles} ${isDark ? inputDarkStyles : inputLightStyles} ${errors.address ? inputErrorStyles : ''}`}
                  />
                  {errors.address && (
                    <p id="address-error" className="text-red-500 text-sm mt-2 flex items-center gap-1" role="alert">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.address}
                    </p>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label 
                    htmlFor="notes"
                    className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-[#4b3a2a]'}`}
                  >
                    {t.checkout.notes}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder={t.checkout.notesPlaceholder}
                    rows={3}
                    className={`${inputBaseStyles} min-h-[80px] py-3 resize-none ${isDark ? inputDarkStyles : inputLightStyles}`}
                  />
                </div>
              </div>
            </fieldset>

            {/* Shipping Options */}
            <div className="mb-6">
              <h3 className={`font-bold text-lg mb-4 ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                {t.orderForm.shipping}
              </h3>
              <div className={`rounded-xl border overflow-hidden ${
                isDark
                  ? 'border-slate-600 bg-slate-700/40 divide-y divide-slate-600'
                  : 'border-[#f2bfd4] bg-white/80 divide-y divide-[#f2bfd4]'
              }`}>
                {shippingOptions.map((option) => {
                  const isSelected = option.id === selectedShippingId;
                  return (
                    <label
                      key={option.id}
                      className={`flex items-center justify-between gap-4 px-4 py-3 transition-all ${
                        isSelected
                          ? isDark
                            ? 'bg-primary/10'
                            : 'bg-[#fff1f7]'
                          : ''
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                            isSelected
                              ? 'border-primary'
                              : isDark
                                ? 'border-slate-500'
                                : 'border-[#d8a4bb]'
                          }`}
                          aria-hidden="true"
                        >
                          <span className={`h-2 w-2 rounded-full ${isSelected ? 'bg-primary' : 'bg-transparent'}`} />
                        </span>
                        <span className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-[#2d1c2b]'}`}>
                          {option.label}
                        </span>
                      </span>
                      <span className={`text-sm font-bold ${isSelected ? 'text-primary' : isDark ? 'text-slate-200' : 'text-[#2d1c2b]'}`}>
                        ৳{option.price}
                      </span>
                      <input
                        type="radio"
                        name="shipping"
                        value={option.id}
                        checked={isSelected}
                        onChange={() => setSelectedShippingId(option.id)}
                        className="sr-only"
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Order Summary */}
            <div className="mb-6">
              <h3 className={`font-bold text-lg mb-4 ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                {t.orderForm.yourOrder}
              </h3>
              <div className={`p-4 rounded-xl space-y-3 ${isDark ? 'bg-slate-700/50' : 'bg-[#fff7fb]'}`}>
                {selectedColors.length > 0 ? (
                  selectedColors.map((color) => (
                    <div key={`summary-${color.id}`} className="flex justify-between items-center">
                      <span className={isDark ? 'text-slate-300' : 'text-[#7b5a6a]'}>
                        {product.name} · {color.label} × {colorQuantities[color.id]}
                      </span>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                        ৳{product.price * colorQuantities[color.id]}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-between items-center">
                    <span className={isDark ? 'text-slate-300' : 'text-[#7b5a6a]'}>
                      {product.name}
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                      ৳0
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className={isDark ? 'text-slate-300' : 'text-[#7b5a6a]'}>
                    {t.checkout.delivery} · {selectedShipping?.label}
                  </span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                    ৳{deliveryCharge}
                  </span>
                </div>
                <div className={`border-t pt-3 flex justify-between items-center ${isDark ? 'border-slate-600' : 'border-[#f2bfd4]'}`}>
                  <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                    {t.checkout.total}
                  </span>
                  <span className="text-primary font-bold text-2xl">৳{grandTotal}</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className={`p-4 rounded-xl border-2 mb-6 ${
              isDark 
                ? 'bg-primary/10 border-primary/30' 
                : 'bg-primary/5 border-primary/20'
            }`}>
              <div className="flex items-center gap-4">
                <span className="text-3xl" role="img" aria-label="Cash">💵</span>
                <div>
                  <p className={`font-bold ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                    {t.orderForm.cashOnDelivery}
                  </p>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}`}>
                    {t.orderForm.codDescription}
                  </p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div 
                className="mb-6 p-4 bg-red-500/10 border-2 border-red-500/30 rounded-xl"
                role="alert"
              >
                <p className="text-red-500 font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {submitError}
                </p>
              </div>
            )}

            {/* Privacy Note */}
            <p className={`text-sm text-center mb-6 ${isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}`}>
              {t.orderForm.privacyNote}
            </p>

            {/* Submit Button - 56px height for prominent CTA */}
            <button
              type="submit"
              disabled={isProcessing}
              className="w-full min-h-[56px] rounded-full border-2 border-[#f2a1c4] bg-[#E24C8B] text-white font-bold text-lg shadow-[0_12px_25px_-15px_rgba(226,76,139,0.85)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 hover:bg-[#C83E78] hover:shadow-[0_16px_28px_-16px_rgba(226,76,139,0.9)] hover:scale-[1.01]"
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t.orderForm.processing}</span>
                </>
              ) : (
                <>
                  <span>{t.orderForm.placeOrder}</span>
                  <span className="bg-white/20 px-4 py-1.5 rounded-full text-base font-bold">
                    ৳{grandTotal}
                  </span>
                </>
              )}
            </button>
          </form>
        ) : (
          /* Success State */
          <div 
            className={`rounded-2xl border p-8 sm:p-10 text-center shadow-card ${
              isDark 
                ? 'bg-slate-800 border-slate-700' 
                : 'bg-white/80 border-[#f2bfd4]'
            }`}
            role="alert"
            aria-live="polite"
          >
            <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
              {t.orderForm.orderSuccess}
            </h3>
            <p className={`mb-8 text-lg ${isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}`}>
              {t.orderForm.thankYou}
            </p>
            
            <div className={`p-6 rounded-xl mb-8 ${isDark ? 'bg-slate-700/50' : 'bg-[#fff7fb]'}`}>
              <dl className="space-y-4">
                <div className="flex justify-between items-center">
                  <dt className={isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}>
                    {t.orderForm.orderNumber}
                  </dt>
                  <dd className={`font-bold ${isDark ? 'text-white' : 'text-[#2d1c2b]'}`}>
                    {orderNumber}
                  </dd>
                </div>
                <div className="flex justify-between items-center">
                  <dt className={isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}>
                    {t.checkout.phone}
                  </dt>
                  <dd className={isDark ? 'text-white' : 'text-[#2d1c2b]'}>
                    +88{formData.phone}
                  </dd>
                </div>
                <div className="flex justify-between items-center">
                  <dt className={isDark ? 'text-slate-400' : 'text-[#7b5a6a]'}>
                    {t.checkout.total}
                  </dt>
                  <dd className="text-primary font-bold text-xl">
                    ৳{grandTotal}
                  </dd>
                </div>
              </dl>
            </div>

            <button
              onClick={handleNewOrder}
              className="px-10 py-4 min-h-[56px] rounded-full border-2 border-[#f2a1c4] bg-[#E24C8B] text-white font-bold shadow-[0_12px_25px_-15px_rgba(226,76,139,0.85)] transition-all duration-200 hover:bg-[#C83E78] hover:shadow-[0_16px_28px_-16px_rgba(226,76,139,0.9)]"
            >
              {t.checkout.continueShopping}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderForm;


